import { DmnModdle, type ModdleElement } from 'dmn-moddle';
import { contentDigest } from '../lib/digest';
import type {
  DmnDecision,
  DmnDecisionTable,
  DmnDefinitionsModel,
  DmnKnowledgeSource,
  DmnRule,
  DmnWarning,
} from './types';

/**
 * 読む属性だけを写したmoddle要素の型。moddleは全属性をoptionalで返すため、
 * ここで受けてから表示モデルへ既定値付きで詰め替える。
 */
interface DmnElementReference {
  href?: string;
}

interface TextElement extends ModdleElement {
  id?: string;
  text?: string;
}

interface InputClauseElement extends ModdleElement {
  id?: string;
  label?: string;
  inputExpression?: TextElement & { typeRef?: string };
}

interface OutputClauseElement extends ModdleElement {
  id?: string;
  label?: string;
  name?: string;
  typeRef?: string;
}

interface DecisionRuleElement extends ModdleElement {
  id?: string;
  inputEntry?: TextElement[];
  outputEntry?: TextElement[];
  /** annotationEntryはid属性を持たない。列との対応は出現順だけ。 */
  annotationEntry?: { text?: string }[];
}

interface DecisionTableElement extends ModdleElement {
  id?: string;
  hitPolicy?: string;
  input?: InputClauseElement[];
  output?: OutputClauseElement[];
  annotation?: { name?: string }[];
  rule?: DecisionRuleElement[];
}

interface DrgElement extends ModdleElement {
  id?: string;
  name?: string;
  description?: string;
  /** knowledgeSourceのみ持つ。出典の所在地であり取得先ではない。 */
  locationURI?: string;
  authorityRequirement?: { requiredAuthority?: DmnElementReference }[];
  decisionLogic?: ModdleElement;
}

interface DefinitionsElement extends ModdleElement {
  id?: string;
  name?: string;
  namespace?: string;
  drgElement?: DrgElement[];
  dmnDI?: { diagrams?: { diagramElements?: { dmnElementRef?: { id?: string } }[] }[] };
}

/**
 * 定義XMLを出典索引付きの表示モデルへ変換する。
 *
 * 式は評価しない。inputEntryにはFlowableのJUEL（`${...}`）と素の比較・引用文字列が混在し、
 * FEELとして解釈すると原文と違う意味を表示してしまうため、文字列のまま持つ。
 */
export async function parseDmnDefinitions(xml: string): Promise<DmnDefinitionsModel> {
  const moddle = new DmnModdle();
  const { rootElement } = await moddle.fromXML<DefinitionsElement>(xml, 'dmn:Definitions');

  const drgElements = rootElement.drgElement ?? [];
  const warnings: DmnWarning[] = [];

  const knowledgeSources: Record<string, DmnKnowledgeSource> = {};
  for (const element of drgElements) {
    if (element.$type !== 'dmn:KnowledgeSource') continue;
    const id = element.id ?? '';
    const name = element.name ?? '';
    knowledgeSources[id] = {
      id,
      name,
      locationURI: element.locationURI ?? null,
      description: (element.description ?? '').trim(),
      // DMNには出典不明を表す属性が無く、スキル規約がnameへ「出典不明」と書く
      // （modeling-business-process/SKILL.md）。部分一致を「不明」まで広げると
      // 「取得日不明」のような通常の出典まで拾うので、規約の語そのもので判定する。
      // BPMN側はrelationship@typeという別の根拠で判定するので、ヘルパを共有しない。
      unknown: name.includes('出典不明'),
    };
  }

  const referencedKnowledgeSourceIds = new Set<string>();
  const decisions: DmnDecision[] = [];
  for (const element of drgElements) {
    if (element.$type !== 'dmn:Decision') continue;
    const decisionId = element.id ?? '';
    const knowledgeSourceIds: string[] = [];
    const danglingAuthorityRefs: string[] = [];

    for (const requirement of element.authorityRequirement ?? []) {
      // requiredDecision / requiredInput は出典ではないので、requiredAuthorityだけを見る。
      const href = requirement.requiredAuthority?.href;
      if (href === undefined) continue;
      // 参照は同一DMN文書内に限る。'#'が無いhrefは外部参照なので解決対象にしない。
      const referenceId = href.startsWith('#') ? href.slice(1) : href;
      if (Object.hasOwn(knowledgeSources, referenceId)) {
        knowledgeSourceIds.push(referenceId);
        referencedKnowledgeSourceIds.add(referenceId);
      } else {
        danglingAuthorityRefs.push(referenceId);
        warnings.push({
          code: 'dangling_authority_ref',
          message: `判定「${decisionId}」のrequiredAuthority "${href}" に対応するknowledgeSourceがありません`,
          ref: decisionId,
        });
      }
    }

    const decisionLogic = element.decisionLogic;
    decisions.push({
      id: decisionId,
      name: element.name ?? '',
      description: (element.description ?? '').trim(),
      knowledgeSourceIds,
      danglingAuthorityRefs,
      table:
        decisionLogic?.$type === 'dmn:DecisionTable'
          ? readDecisionTable(decisionLogic as DecisionTableElement, warnings)
          : null,
    });
  }

  const shapedElementIds = new Set<string>();
  for (const diagram of rootElement.dmnDI?.diagrams ?? []) {
    for (const diagramElement of diagram.diagramElements ?? []) {
      const shapedId = diagramElement.dmnElementRef?.id;
      if (shapedId !== undefined) shapedElementIds.add(shapedId);
    }
  }

  for (const knowledgeSource of Object.values(knowledgeSources)) {
    if (!referencedKnowledgeSourceIds.has(knowledgeSource.id)) {
      warnings.push({
        code: 'unreferenced_knowledge_source',
        message: `出典「${knowledgeSource.name}」はどの判定からもauthorityRequirementで参照されていません`,
        ref: knowledgeSource.id,
      });
    }
    if (!shapedElementIds.has(knowledgeSource.id)) {
      warnings.push({
        code: 'missing_dmndi',
        message: `出典「${knowledgeSource.name}」のDMNShapeが無く、関連図に描かれません`,
        ref: knowledgeSource.id,
      });
    }
  }

  return {
    id: rootElement.id ?? '',
    name: rootElement.name ?? '',
    // definitions@namespace。業務側が決める識別子で、DMN 1.3のMODEL名前空間とは別物。
    namespace: rootElement.namespace ?? '',
    sourceDigest: contentDigest(xml),
    decisions,
    knowledgeSources,
    warnings,
  };
}

function readDecisionTable(
  element: DecisionTableElement,
  warnings: DmnWarning[],
): DmnDecisionTable {
  const tableId = element.id ?? '';
  const annotationLabels = (element.annotation ?? []).map((column) => column.name ?? '');

  if (annotationLabels.length === 0) {
    warnings.push({
      code: 'missing_annotation_column',
      message: `判定表「${tableId}」にannotation列が無く、ルールごとの根拠を読めません`,
      ref: tableId,
    });
  }

  const rules: DmnRule[] = (element.rule ?? []).map((rule) => {
    const ruleId = rule.id ?? '';
    // annotationEntryが列数より少ないこともあるが、原文に無いセルを補わない。
    // 列との対応は出現順なので、表示側はannotationLabelsと同じ添字で読む。
    const annotations = (rule.annotationEntry ?? []).map((entry) => (entry.text ?? '').trim());
    const hasEmptyAnnotation = annotationLabels.some(
      (_label, columnIndex) => (annotations[columnIndex] ?? '') === '',
    );
    if (hasEmptyAnnotation) {
      warnings.push({
        code: 'empty_annotation_entry',
        message: `ルール「${ruleId}」に根拠が書かれていないannotation列があります`,
        ref: ruleId,
      });
    }
    return {
      id: ruleId,
      inputEntries: (rule.inputEntry ?? []).map((entry) => (entry.text ?? '').trim()),
      outputEntries: (rule.outputEntry ?? []).map((entry) => (entry.text ?? '').trim()),
      annotations,
    };
  });

  return {
    id: tableId,
    hitPolicy: element.hitPolicy ?? 'UNIQUE',
    inputs: (element.input ?? []).map((input) => ({
      id: input.id ?? '',
      label: input.label ?? '',
      expression: (input.inputExpression?.text ?? '').trim(),
      typeRef: input.inputExpression?.typeRef ?? '',
    })),
    outputs: (element.output ?? []).map((output) => ({
      id: output.id ?? '',
      label: output.label ?? '',
      name: output.name ?? '',
      typeRef: output.typeRef ?? '',
    })),
    annotationLabels,
    rules,
  };
}
